import React, { useState } from 'react';
import {
  Button,
  Snackbar,
  Alert,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Stop as StopIcon,
  VolumeOff as VolumeOffIcon
} from '@mui/icons-material';
import { autoWebViewJs } from "@droid/android";
import { Log } from "@fanfanlo";
import { trackingPrint } from "@app/model";
import { stopAllHandler } from './stopAllHandler';

const fileLog = new Log(false, 'EmergencyStopButton');

interface EmergencyStopButtonProps {
  variant?: 'button' | 'fab';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

/**
 * 紧急停止按钮组件
 * 快速停止所有紧急呼救相关的音频和震动
 */
const EmergencyStopButton: React.FC<EmergencyStopButtonProps> = ({ 
  variant = 'button',
  size = 'medium',
  fullWidth = false
}) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /**
   * 检查是否在Android WebView环境中
   */
  const isAndroidWebView = (): boolean => {
    try {
      return !!(autoWebViewJs && typeof autoWebViewJs.callScript === 'function');
    } catch (e) {
      fileLog.error('检查WebView环境时出错:', e);
      return false;
    }
  };

  /**
   * 停止所有紧急呼救音频和震动
   */
  const handleStopAllEmergencyAudio = async (): Promise<void> => {
    setLoading(true);
    setMessage(null);

    // 添加按钮点击埋点
    trackingPrint({params:{
      type:"用户操作",
      title:"紧急停止",
      subtitle:"停止按钮-点击",
      extra:{
        buttonType: variant,
        timestamp: Date.now(),
        isAndroidWebView: isAndroidWebView()
      }
    }});

    try {
      const bl = stopAllHandler();
      if (bl){
        // 成功埋点
        trackingPrint({params:{
          type:"系统事件",
          title:"紧急停止",
          subtitle:"停止成功",
          extra:{
            result: "success",
            timestamp: Date.now()
          }
        }});
        
        setMessage({
          type: 'success',
          text: '已停止所有紧急音频和震动'
        });
      }else{
        // 失败埋点
        trackingPrint({params:{
          type:"系统异常",
          title:"紧急停止",
          subtitle:"停止失败",
          extra:{
            result: "failed",
            timestamp: Date.now(),
            reason: "stopAllHandler返回false"
          }
        }});
        
        setMessage({
          type: 'error',
          text: `停止失败`
        });
      }
    } catch (error) {
      // 异常埋点
      trackingPrint({params:{
        type:"系统异常",
        title:"紧急停止",
        subtitle:"停止异常",
        extra:{
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          timestamp: Date.now()
        }
      }});
      
      setMessage({
        type: 'error',
        text: '停止时发生异常'
      });
    }
    
    setLoading(false);
  };

  /**
   * 关闭消息提示
   */
  const handleCloseMessage = () => {
    setMessage(null);
  };

  // 浮动操作按钮样式
  if (variant === 'fab') {
    return (
      <>
        <Tooltip title="停止所有紧急音频和震动" placement="left">
          <Fab
            color="error"
            onClick={handleStopAllEmergencyAudio}
            disabled={loading}
            size={size}
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 16,
              zIndex: 1000
            }}
          >
            {loading ? <VolumeOffIcon /> : <StopIcon />}
          </Fab>
        </Tooltip>

        <Snackbar
          open={!!message}
          autoHideDuration={4000}
          onClose={handleCloseMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseMessage} 
            severity={message?.type || 'info'}
            sx={{ width: '100%' }}
          >
            {message?.text}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // 普通按钮样式
  return (
    <>
      <Button
        variant="contained"
        color="error"
        size={size}
        fullWidth={fullWidth}
        startIcon={loading ? <VolumeOffIcon /> : <StopIcon />}
        onClick={handleStopAllEmergencyAudio}
        disabled={loading}
        sx={{ mb: 1 }}
      >
        🔕 静音
      </Button>

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseMessage} 
          severity={message?.type || 'info'}
          sx={{ width: '100%' }}
        >
          {message?.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmergencyStopButton;